import { cloneDeep, map } from "lodash-es";
import { defaultsDeepPreserveArrays } from "@/helpers/utility/CollectionFunctions";
import {
  createStoreItem,
  fillDefaultValues
} from "./ItemStore";

import type { ItemStoreState } from "./ItemStore";
import type { StoreItem } from "./StoreItem";
import type { SetOptional } from "type-fest";

/**
 * Migrate an item store from an old version to the current version of the store spec.
 * All items of the store will be migrated.
 */
export function migrateItemStore(
  currentStoreState: ItemStoreState,
  baseStoreState: ItemStoreState
) {
  // save a reference to the current (unmigrated) items
  const currentItems = currentStoreState.items;

  // create order array if there's none:
  if (currentStoreState.order == null) {
    currentStoreState.order = map(currentItems, "id");
  }

  // create a new store state with the default structure as base.
  const newStoreState = defaultsDeepPreserveArrays(
    {
      ...currentStoreState,
      // remove the items, we'll add them back in later,
      // after they have been migrated.
      items: {}
    },
    baseStoreState
  );

  // remove any default items, since we already have a current item list.
  newStoreState.items = {};

  // Always keep the item structure from the base store state.
  newStoreState.itemStructure = cloneDeep(baseStoreState.itemStructure);

  for (const itemId in currentItems) {
    // add the items back in, with the default structure as base.
    newStoreState.items[itemId] = migrateStoreItem(
      currentItems[itemId],
      baseStoreState.itemStructure,
      newStoreState
    );
  }

  return newStoreState;
}

type ItemStoreFunctions<T extends StoreItem = StoreItem> = {
  create(data: Partial<T>): Promise<T>;
};

/**
 * Get all migrations from the "/migrations" subfolders of all items.
 */
const itemMigrationFiles = import.meta.glob("./*/migrations/*.ts");
const itemMigrations = new Map<string, ItemMigration[]>();
for (const path in itemMigrationFiles) {
  itemMigrationFiles[path]().then((mod) => {
    if (!mod) return;
    if (typeof mod !== "object") return;
    if ("default" in mod) {
      const itemMigration = mod.default as ItemMigration;
      const itemType = path.match(/\.\/(.*)\/migrations/)?.[1];
      if (itemType) {
        if (!itemMigrations.has(itemType)) {
          itemMigrations.set(itemType, []);
        }
        itemMigrations.get(itemType)?.push(itemMigration);
      }
    }
  });
}

/**
 * Tranfer an item from an old store to the new store structure.
 * Applies ALL migrations for the item type.
 * Migrations are defined in the "/migrations" subfolder of the item.
 *
 * @param currentItemState The item to migrate.
 * @param defaultItemStructure An object containing all default values for the item.
 * @param itemStoreState The item store state, data only.
 * @param functions Optional. Functions that can be used to create new items.
 */
export function migrateStoreItem<T extends StoreItem = StoreItem>(
  currentItemState: object,
  defaultItemStructure: T,
  itemStoreState: ItemStoreState<T>,
  functions?: ItemStoreFunctions<T>
) {
  const newItem = defaultsDeepPreserveArrays(
    currentItemState,
    defaultItemStructure
  ) as T;

  const itemType = newItem.type;

  const migrations: ItemMigration[] = itemMigrations.get(itemType) ?? [];

  if (!functions) {
    functions = {
      create: async (data) => createStoreItem(itemStoreState, data)
    };
  }

  for (const migrate of migrations) {
    migrate(itemStoreState, currentItemState, newItem);
  }

  // remove all properties that are not in the default item structure.
  for (const prop in newItem) {
    if (!(prop in defaultItemStructure)) {
      // console.debug(prop, "is not in", defaultItemStructure.type);
      delete newItem[prop];
    }
  }

  return newItem;
}

type ItemMigrationFunction<
  T extends StoreItem = StoreItem,
  O extends object = object
> = (
  itemStore: ItemStoreState<T>,
  // id is optional, because it could
  // be removed on import.
  currentItem: OptionalId<O | T>,
  newItem: T,
  itemStoreFunctions: ItemStoreFunctions<T>
) => void;

export type ItemMigration<
  T extends StoreItem = StoreItem,
  O extends object = object
> = (
  itemStore: ItemStoreState<T>,
  currentItem: OptionalId<O | T>,
  newItem?: T,
  itemStoreFunctions?: ItemStoreFunctions<T>
) => T;

/**
 * Define a migration for an item type.
 * Item migrations are used to transfer old model data to a new version,
 * but ALSO to handle importing single model parts from old versions.
 * (e.g. importing a single action from an old model)
 *
 * (!) Migrations change the newItem object in place.
 *
 * Item migrations DO NOT touch stores other than the item store.
 * If you need that, consider using a general store migration instead.
 * Store migrations are located under src/app/editor/store/migrations.
 *
 * This is nothing but a wrapper that defines default values for the parameters.
 */
export function defineItemMigration<
  T extends StoreItem,
  O extends object = object
>(migrationFunction: ItemMigrationFunction<T, O>): ItemMigration<T, O> {
  return (
    itemStoreState: ItemStoreState<T>,
    currentItem: OptionalId<O | T>,
    newItem?: T,
    functions?: ItemStoreFunctions<T>
  ) => {
    if (!newItem) {
      newItem = fillDefaultValues(currentItem, itemStoreState.itemStructure);
    }

    if (!functions) {
      functions = {
        create: async (data) => createStoreItem(itemStoreState, data)
      };
    }

    migrationFunction(itemStoreState, currentItem, newItem, functions);

    return newItem;
  };
}

type OptionalId<T extends object> = SetOptional<T & { id?: number }, "id">;
